import { useState, useEffect, Fragment } from "react";
import { listObservations, getAircraftSummary } from "./api";
import type { Observation, AircraftSummary } from "./api";
import type { TZ } from "./lib/time";
import { formatTime } from "./lib/time";

interface Props {
  hex: string;
  start: number;
  end: number;
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return "<1m";
}

export default function FlightDetailPage({ hex, start, end }: Props) {
  const [tz, setTz] = useState<TZ>(() => (localStorage.getItem("tz") as TZ) ?? "utc");
  const [summary, setSummary] = useState<AircraftSummary | null>(null);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAircraftSummary(hex, start, end),
      listObservations({ hex, start, end, limit: 5000 }),
    ])
      .then(([s, page]) => {
        if (cancelled) return;
        setSummary(s);
        setObservations(page.items.sort((a, b) => a.observed_at - b.observed_at));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(String(err));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [hex, start, end]);

  const handleTzToggle = () => {
    const newTz: TZ = tz === "utc" ? "local" : "utc";
    setTz(newTz);
    localStorage.setItem("tz", newTz);
  };

  const title = summary?.callsigns[0] ?? hex.toUpperCase();

  const stats: [string, string][] = [
    ["Duration", formatDuration(end - start)],
    ["Positions", summary ? summary.position_count.toLocaleString() : "—"],
    [
      "Altitude",
      summary?.alt_min != null && summary?.alt_max != null
        ? `${summary.alt_min.toLocaleString()}–${summary.alt_max.toLocaleString()} ft`
        : "—",
    ],
    ["Max GS", summary?.gs_max != null ? `${summary.gs_max} kt` : "—"],
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <a
          href="/"
          className="text-sm text-muted-foreground hover:text-foreground shrink-0"
        >
          ← Map
        </a>
        <div className="h-4 w-px bg-border shrink-0" />
        <span className="font-bold text-primary">{title}</span>
        {summary?.registration && (
          <span className="text-sm text-muted-foreground">{summary.registration}</span>
        )}
        {summary?.description && (
          <span className="text-sm text-muted-foreground">· {summary.description}</span>
        )}
        <div className="ml-auto">
          <button
            onClick={handleTzToggle}
            className="text-xs px-2 py-1 rounded border border-border bg-muted hover:bg-muted/80 font-mono"
            title="Toggle UTC / local time"
          >
            {tz === "utc" ? "UTC" : "Local"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading && <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>}
        {error && <p className="text-sm text-destructive py-8 text-center">Error: {error}</p>}

        {!loading && !error && (
          <>
            {/* Stats strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {stats.map(([label, value]) => (
                <div key={label} className="bg-muted rounded px-3 py-2">
                  <div className="text-[0.6rem] text-muted-foreground uppercase tracking-wide">{label}</div>
                  <div className="text-sm font-semibold">{value}</div>
                </div>
              ))}
            </div>

            {/* Time window */}
            <p className="text-xs text-muted-foreground mb-4 font-mono">
              {formatTime(start, tz)} → {formatTime(end, tz)}
            </p>

            {/* Observations table */}
            <div className="rounded border border-border overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr className="text-left text-muted-foreground">
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Time</th>
                    <th className="px-3 py-2 font-medium">Lat</th>
                    <th className="px-3 py-2 font-medium">Lon</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Alt (ft)</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">GS (kt)</th>
                    <th className="px-3 py-2 font-medium whitespace-nowrap">Track (°)</th>
                    <th className="px-3 py-2 font-medium">Squawk</th>
                    <th className="px-3 py-2 font-medium">Flags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {observations.map((obs) => (
                    <Fragment key={obs.id}>
                      <tr
                        className="hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() =>
                          setExpandedRow(expandedRow === obs.id ? null : obs.id)
                        }
                      >
                        <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                          {formatTime(obs.observed_at, tz)}
                        </td>
                        <td className="px-3 py-1.5 font-mono">
                          {obs.lat != null ? obs.lat.toFixed(4) : "—"}
                        </td>
                        <td className="px-3 py-1.5 font-mono">
                          {obs.lon != null ? obs.lon.toFixed(4) : "—"}
                        </td>
                        <td className="px-3 py-1.5 font-mono">
                          {obs.alt_baro != null ? obs.alt_baro.toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-1.5 font-mono">{obs.ground_speed ?? "—"}</td>
                        <td className="px-3 py-1.5 font-mono">{obs.track ?? "—"}</td>
                        <td className="px-3 py-1.5 font-mono">{obs.squawk ?? "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">
                          {[
                            obs.alert && "alert",
                            obs.spi && "spi",
                            obs.on_ground && "gnd",
                          ]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </td>
                      </tr>
                      {expandedRow === obs.id && (
                        <tr>
                          <td colSpan={8} className="px-3 py-2 bg-muted/30">
                            <pre className="text-[0.65rem] text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all">
                              {JSON.stringify(obs, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              {observations.length === 0 && !loading && (
                <p className="text-sm text-muted-foreground p-4 text-center">
                  No observations found.
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {observations.length.toLocaleString()} observations · click a row to expand raw data
            </p>
          </>
        )}
      </div>
    </div>
  );
}
